import React, {memo, type KeyboardEvent, type MouseEvent, type ReactNode} from 'react'

export type OmicsToolCollapsibleRowProps = {
  className?: string
  title: string
  subtitle?: string
  meta?: string
  pending?: boolean
  failed?: boolean
  open: boolean
  onToggle: () => void
  children?: ReactNode
}

/** Shared compact collapsible used by fallback, keyed tools, and code-exec. */
export const OmicsToolCollapsibleRow: React.FC<OmicsToolCollapsibleRowProps> = memo(
  ({className, title, subtitle, meta, pending = false, failed = false, open, onToggle, children}) => {
    const statusClass = pending ? 'is-pending' : failed ? 'is-error' : 'is-success'
    const extra = className ? ` ${className}` : ''

    const toggle = (e: MouseEvent | KeyboardEvent) => {
      e.preventDefault()
      onToggle()
    }

    return (
      <div className={`ai-chat-tool-row collapsible-section${extra} ${statusClass}`} data-open={open}>
        <div
          role='button'
          tabIndex={0}
          aria-expanded={open}
          aria-busy={pending || undefined}
          className='section-summary ai-chat-tool-summary'
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggle(e)
          }}
        >
          <i className={`fas collapsible-chevron ${open ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden />
          <i
            className={`fas ${pending ? 'fa-spinner fa-spin' : failed ? 'fa-triangle-exclamation' : 'fa-check'}`}
            aria-hidden
          />
          <span className='ai-chat-tool-summary-text'>
            <span className='ai-chat-tool-summary-title'>{title}</span>
            {subtitle ? <span className='ai-chat-tool-summary-subtitle'>{subtitle}</span> : null}
          </span>
          {meta ? <span className='ai-chat-tool-summary-meta'>{meta}</span> : null}
        </div>
        {open && children ? <div className='section-details ai-chat-tool-details'>{children}</div> : null}
      </div>
    )
  },
)

OmicsToolCollapsibleRow.displayName = 'OmicsToolCollapsibleRow'
